import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../../context/userAuthContext";
import { collection, getDocs, query, where, orderBy, doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  Shield,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  ArrowRight,
  Calendar,
  User,
  Eye,
  Edit,
  TrendingUp,
  Users,
} from "lucide-react";

const AdminPoliciesDashboard = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    if (user) {
      checkUserType();
      loadPolicies();
    }
  }, [user, filterStatus, filterType]);

  const checkUserType = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, "Users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserType(userData.type || user.type || null);
      } else {
        setUserType(user.type || null);
      }
    } catch (error) {
      console.error("Error checking user type:", error);
      setUserType(user.type || null);
    }
  };

  const loadPolicies = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Load all policies and filter client-side (more reliable)
      const policiesRef = collection(db, "policies");
      const snapshot = await getDocs(policiesRef);
      
      const allPolicies = [];
      snapshot.forEach((doc) => {
        allPolicies.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Apply filters client-side
      let filteredPolicies = allPolicies;
      
      if (filterStatus !== "all") {
        filteredPolicies = filteredPolicies.filter((p) => p.estado === filterStatus);
      }
      
      if (filterType !== "all") {
        filteredPolicies = filteredPolicies.filter((p) => p.tipoPoliza === filterType);
      }

      // Sort by createdAt descending
      filteredPolicies.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || a.createdAt?.seconds 
          ? new Date(a.createdAt.seconds * 1000) 
          : a.createdAt 
            ? new Date(a.createdAt) 
            : new Date(0);
        const bDate = b.createdAt?.toDate?.() || b.createdAt?.seconds 
          ? new Date(b.createdAt.seconds * 1000) 
          : b.createdAt 
            ? new Date(b.createdAt) 
            : new Date(0);
        return bDate - aDate;
      });

      setPolicies(filteredPolicies);

      // Calculate statistics from all policies
      const stats = {
        policies: {
          total: allPolicies.length,
          pendiente: allPolicies.filter((p) => p.estado === "pendiente").length,
          aprobada: allPolicies.filter((p) => p.estado === "aprobada").length,
          rechazada: allPolicies.filter((p) => p.estado === "rechazada").length,
          en_revision: allPolicies.filter((p) => p.estado === "en_revision").length,
        },
      };

      setStatistics(stats);
    } catch (error) {
      console.error("Error loading policies:", error);
      setPolicies([]);
      setStatistics({
        policies: {
          total: 0,
          pendiente: 0,
          aprobada: 0,
          rechazada: 0,
          en_revision: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePolicyStatus = async (policyId, newStatus, reason) => {
    if (!reason || reason.trim() === "") {
      alert("Por favor proporciona una razón para el cambio de estado");
      return;
    }

    try {
      const policyRef = doc(db, "policies", policyId);
      const policyDoc = await getDoc(policyRef);

      if (!policyDoc.exists()) {
        alert("Póliza no encontrada");
        return;
      }

      const policy = policyDoc.data();
      const oldStatus = policy.estado;

      // Create history entry
      const historyEntry = {
        timestamp: new Date(),
        changedBy: user.uid,
        changedByEmail: user.email || "Unknown",
        changes: [
          {
            field: "estado",
            oldValue: oldStatus,
            newValue: newStatus,
          },
        ],
        oldStatus: oldStatus,
        newStatus: newStatus,
        reason: reason || `Cambio de estado a ${newStatus}`,
        changedByRole: userType || "admin",
      };

      // Update policy directly in Firestore
      await updateDoc(policyRef, {
        estado: newStatus,
        updatedAt: new Date(),
        history: [...(policy.history || []), historyEntry],
        reviewedBy: user.uid,
        reviewedAt: new Date(),
      });

      loadPolicies();
      alert("Estado actualizado exitosamente");
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error al actualizar estado: " + error.message);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: {
        icon: Clock,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Pendiente",
      },
      aprobada: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Aprobada",
      },
      rechazada: {
        icon: XCircle,
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Rechazada",
      },
      en_revision: {
        icon: AlertCircle,
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "En Revisión",
      },
    };

    const config = statusConfig[estado] || statusConfig.pendiente;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getPolicyTypeLabel = (tipo) => {
    const types = {
      seguro_viajero: "Seguro de Viajero",
      seguro_auto: "Seguro de Auto",
      seguro_hogar: "Seguro de Hogar",
      seguro_vida: "Seguro de Vida",
      seguro_gmm: "Gastos Médicos Mayores",
    };
    return types[tipo] || tipo;
  };

  const filteredPolicies = policies.filter((policy) => {
    const matchesSearch =
      getPolicyTypeLabel(policy.tipoPoliza)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      policy.nombreCompleto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso no autorizado</h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  // Show warning if not admin, but allow access
  const isAdmin = userType === "admin" || user.type === "admin";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg shadow-md">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent">
                  Panel de Administración - Pólizas
                </h1>
                <p className="text-xs text-slate-600">Gestiona todas las solicitudes de seguros</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Admin Warning */}
        {!isAdmin && (
          <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800 mb-1">
                  Modo de Vista Previa
                </p>
                <p className="text-xs text-yellow-700">
                  Estás viendo el panel de administración en modo de vista previa. Algunas funciones pueden estar limitadas.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Total</span>
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {statistics.policies?.total || 0}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-yellow-700">Pendientes</span>
                <Clock className="w-4 h-4 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-yellow-800">
                {statistics.policies?.pendiente || 0}
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-700">Aprobadas</span>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-800">
                {statistics.policies?.aprobada || 0}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">En Revisión</span>
                <AlertCircle className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-800">
                {statistics.policies?.en_revision || 0}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-md border border-slate-200/50 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, email o tipo..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_revision">En Revisión</option>
              <option value="aprobada">Aprobada</option>
              <option value="rechazada">Rechazada</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
            >
              <option value="all">Todos los tipos</option>
              <option value="seguro_viajero">Seguro de Viajero</option>
              <option value="seguro_auto">Seguro de Auto</option>
              <option value="seguro_hogar">Seguro de Hogar</option>
              <option value="seguro_vida">Seguro de Vida</option>
              <option value="seguro_gmm">Gastos Médicos Mayores</option>
            </select>
          </div>
        </div>

        {/* Policies List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-[#5fc500] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredPolicies.length > 0 ? (
          <div className="space-y-3">
            {filteredPolicies.map((policy) => {
              const policyDate = policy.createdAt?.toDate
                ? policy.createdAt.toDate()
                : policy.createdAt
                  ? new Date(policy.createdAt.seconds * 1000)
                  : null;

              return (
                <div
                  key={policy.id}
                  className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-slate-900">
                          {getPolicyTypeLabel(policy.tipoPoliza)}
                        </h3>
                        {getStatusBadge(policy.estado)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{policy.nombreCompleto || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {policyDate
                              ? policyDate.toLocaleDateString("es-MX")
                              : "Fecha no disponible"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span>{policy.userEmail || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/polizas/${policy.id}`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/polizas/edit/${policy.id}`)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      {policy.estado !== "aprobada" && (
                        <select
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus !== policy.estado) {
                              const reason = prompt(
                                `Razón para cambiar el estado a "${newStatus}":`
                              );
                              if (reason) {
                                updatePolicyStatus(policy.id, newStatus, reason);
                              }
                              e.target.value = policy.estado; // Reset select
                            }
                          }}
                          className="px-3 py-1 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] bg-white"
                          defaultValue={policy.estado}
                        >
                          <option value="pendiente">Pendiente</option>
                          <option value="en_revision">En Revisión</option>
                          <option value="aprobada">Aprobar</option>
                          <option value="rechazada">Rechazar</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No se encontraron pólizas</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPoliciesDashboard;

